/**
 * @file Seeded randomness. `Math.random()` is BANNED inside `src/` — same seed
 * must always produce a byte-identical level.
 *
 * Per-stage streams are mandatory: derive one `Rng` per generation stage with
 * `streamRng(seed, 'roads')` instead of threading a single generator through
 * everything. Otherwise inserting a stage (or drawing one extra number in an
 * early stage) reshuffles every later stage and the whole city changes.
 */

/** @typedef {import('../types.js').Rng} Rng */

/**
 * Mulberry32 — 32-bit state, fast, good enough for content generation.
 * @param {number} seed Any integer; coerced to uint32.
 * @returns {Rng} Values in [0,1).
 */
export function mulberry32(seed) {
  let s = seed >>> 0;
  return function rng() {
    s = (s + 0x6d2b79f5) | 0;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * FNV-1a style string hash -> uint32. Numbers pass through as uint32 so
 * `hashSeed(1234)` and `hashSeed('1234')` are deliberately different streams.
 * @param {string|number} str
 * @returns {number} uint32
 */
export function hashSeed(str) {
  if (typeof str === 'number') return str >>> 0;
  let h = 2166136261 >>> 0;
  const s = String(str);
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  // Final avalanche so single-character seeds still differ wildly.
  h ^= h >>> 16;
  h = Math.imul(h, 2246822507);
  h ^= h >>> 13;
  h = Math.imul(h, 3266489909);
  h ^= h >>> 16;
  return h >>> 0;
}

/**
 * The canonical way to get a generator for one stage of one level.
 * @param {string|number} seed Master seed.
 * @param {string} [salt] Stage name, e.g. `'roads'`, `'buildings'`.
 * @returns {Rng}
 */
export function streamRng(seed, salt = '') {
  return mulberry32((hashSeed(seed) + hashSeed(salt)) >>> 0);
}

/**
 * @param {Rng} rng
 * @param {number} a
 * @param {number} b
 * @returns {number} Uniform in [a,b).
 */
export const range = (rng, a, b) => a + rng() * (b - a);

/**
 * @param {Rng} rng
 * @param {number} a
 * @param {number} b
 * @returns {number} Uniform INTEGER in [a,b] (inclusive both ends).
 */
export const int = (rng, a, b) => Math.floor(a + rng() * (b - a + 1));

/**
 * @template T
 * @param {Rng} rng
 * @param {T[]} arr
 * @returns {T}
 */
export const pick = (rng, arr) => arr[Math.floor(rng() * arr.length)];

/**
 * @param {Rng} rng
 * @param {number} p Probability in [0,1].
 * @returns {boolean}
 */
export const chance = (rng, p) => rng() < p;

/**
 * Weighted pick.
 * @template T
 * @param {Rng} rng
 * @param {Array<[T, number]>} pairs `[value, weight]` — weights need not sum to 1.
 * @returns {T}
 */
export function weighted(rng, pairs) {
  let total = 0;
  for (let i = 0; i < pairs.length; i++) total += pairs[i][1];
  let r = rng() * total;
  for (let i = 0; i < pairs.length; i++) {
    r -= pairs[i][1];
    if (r <= 0) return pairs[i][0];
  }
  return pairs[pairs.length - 1][0];
}

/**
 * Fisher-Yates. Returns a NEW array; the input is untouched.
 * @template T
 * @param {Rng} rng
 * @param {T[]} arr
 * @returns {T[]}
 */
export function shuffle(rng, arr) {
  const out = arr.slice();
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    const t = out[i];
    out[i] = out[j];
    out[j] = t;
  }
  return out;
}

/**
 * Triangular jitter in (-1,1), biased toward 0 — the right shape for "mostly
 * aligned, occasionally off" placement noise.
 * @param {Rng} rng
 * @returns {number}
 */
export const tri = (rng) => rng() - rng();

/**
 * Random unit vector on the XZ plane.
 * @param {Rng} rng
 * @returns {[number, number]}
 */
export function unitXZ(rng) {
  const a = rng() * Math.PI * 2;
  return [Math.cos(a), Math.sin(a)];
}
